import { Receipt } from "lucide-react";
import ScorecardInfoTip from "./ScorecardInfoTip";
import { fmtNum, fmtMoney, fmtMoneyShort } from "./formatters";

/**
 * Mini benchmark bar chart comparing average ticket size across each
 * business unit (Distribuidor / Retail / Seminuevos). Renders horizontal
 * bars proportional to the highest unit ticket in the scope so the reader
 * can spot which unit anchors the overall Ticket Promedio at a glance.
 *
 * Bars encode the TOTAL avg ticket; the digital-only avg is shown as a
 * secondary chip on the right so the two lenses are always side-by-side.
 */
const UNIT_COLORS = {
  Distribuidor: "#0ea5e9",
  Retail:       "#f4c400",
  Seminuevos:   "#8b5cf6",
  Gobierno:     "#dc2626",
};

export default function TicketPromedioMini({ rows }) {
  if (!rows || rows.length === 0) return null;
  const maxTicket = Math.max(...rows.map(r => r.avg_ticket || 0), 1);

  return (
    <div className="surface p-4 mb-5" data-testid="ticket-promedio-mini">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="kpi-icon-clean" style={{
            width: 28, height: 28, color: "#14b8a6",
            boxShadow: "inset 0 0 0 1px #14b8a633",
          }}>
            <Receipt size={14} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
              Ticket Promedio · Comparación por Unidad de Negocio
              <ScorecardInfoTip
                label="Ticket Promedio por Unidad"
                formula="Σ valor_neto ÷ unidades vendidas — agrupado por unidad (Distribuidor / Retail / Seminuevos)"
                extra="Barra = ticket total de la unidad. Chip 'Digital' = mismo cálculo restringido a ventas atribuidas a leads digitales. Respeta los filtros del período/marca/sucursal aplicados arriba."
                source="sales.valor_neto ÷ count(sales) grouped by tipo_compra/showroom"
              />
            </div>
            <div className="text-[10px] text-gray-500">
              Benchmark side-by-side · barra proporcional a la unidad con ticket más alto
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2.5">
        {rows.map((r) => {
          const pct = ((r.avg_ticket || 0) / maxTicket) * 100;
          const color = UNIT_COLORS[r.unit] || "#64748b";
          return (
            <div key={r.unit}
              className="grid gap-3 items-center"
              style={{ gridTemplateColumns: "120px 1fr auto" }}
              data-testid={`ticket-unit-${r.unit.toLowerCase()}`}>
              <div>
                <div className="text-xs font-bold text-gray-800">{r.unit}</div>
                <div className="text-[10px] text-gray-500">
                  {fmtNum(r.units)} unid. · {fmtNum(r.digital_units)} dig.
                </div>
              </div>
              <div className="relative"
                style={{ height: 24, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}
                title={fmtMoney(r.avg_ticket)}>
                <div style={{
                  width: `${pct}%`, height: "100%", background: color,
                  transition: "width 400ms ease",
                }} />
                <div className="absolute inset-0 flex items-center px-2 text-[11px] font-bold tabular-nums"
                  style={{ color: pct > 55 ? "#fff" : "#111827",
                           mixBlendMode: pct > 55 ? "normal" : "normal",
                           fontFamily: "Manrope, sans-serif" }}>
                  {fmtMoneyShort(r.avg_ticket)}
                </div>
              </div>
              <div className="text-[10px] whitespace-nowrap px-2 py-1 rounded-md"
                style={{ background: `${color}15`, color, fontWeight: 700,
                         border: `1px solid ${color}33` }}
                title={`Digital: ${fmtMoney(r.avg_ticket_digital)}`}>
                Digital: {fmtMoneyShort(r.avg_ticket_digital)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
